<template>
  <div>
    <!-- Stats -->
    <b-row class="mb-3 pt-5">
      <b-col xxl="3" sm="6" v-for="(card, idx) in statsCards" :key="idx">
        <b-card no-body class="card-animate">
          <b-card-body>
            <div class="d-flex justify-content-between">
              <div>
                <p class="fw-medium text-muted mb-0">{{ card.label }}</p>
                <h2 class="mt-4 ff-secondary fw-semibold">
                  <count-to :startVal="0" :endVal="card.value" :duration="2000" />
                  {{ card.suffix }}
                </h2>
              </div>
              <div>
                <div class="avatar-sm flex-shrink-0">
                  <span :class="['avatar-title', card.avatarBg, 'rounded-circle', 'fs-4']">
                    <i :class="card.icon"></i>
                  </span>
                </div>
              </div>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <!-- Filters -->
    <b-card class="mb-3">
      <b-form @submit.prevent>
        <b-row class="g-2 align-items-center">
          <b-col lg="3" sm="12">
            <div class="search-box">
              <input
                type="text"
                class="form-control bg-light border-light"
                placeholder="Search activities..."
                v-model="searchQuery"
                @input="onSearchInput"
              />
              <i class="ri-search-line search-icon"></i>
            </div>
          </b-col>

          <b-col lg="3" sm="6">
            <flat-pickr
              v-model="filterDateRange"
              :config="rangeDateconfig"
              placeholder="Select date range"
              @on-close="fetchActivities"
            />
          </b-col>

          <b-col lg="3" sm="6">
            <Multiselect
              v-model="filterStatus"
              :options="statusSelectOptions"
              placeholder="Select status"
            />
          </b-col>

          <b-col lg="3" sm="12" class="text-end">
            <b-button variant="light" class="me-2" @click="resetFilters">
              <i class="ri-close-line align-bottom"></i> Reset
            </b-button>
            <b-button variant="primary" @click="fetchActivities">
              <i class="ri-filter-line align-bottom"></i> Apply Filters
            </b-button>
          </b-col>

          <!-- <b-col lg="2" sm="12" class="text-end">
            <b-button variant="success" class="w-100" @click="openCreateModal">
              <i class="ri-add-line align-bottom me-1"></i> Create Activity
            </b-button>
          </b-col> -->
        </b-row>
      </b-form>
    </b-card>

    <!-- Table -->
    <b-card>
      <div class="text-center" v-if="loading">
        <b-spinner variant="info" label="Loading..."></b-spinner>
      </div>

      <div v-else>
        <div class="table-responsive table-card mb-4">
          <table class="table align-middle table-nowrap mb-0">
            <thead class="table-light text-muted">
              <tr>
                <th>#</th>
                <th>Activity Number</th>
                <th>Title</th>
                <th>Description</th>
                <th>Created By</th>
                <th>Assigned To</th>
                <th>Due Date</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="(activity, index) in filteredActivities" :key="activity.id ?? index">
                <td>{{ (page - 1) * perPage + index + 1 }}</td>

                <td>
                  <router-link :to="`/activity-details/${activity.slug}/${activity.id}`" class="fw-medium link-primary">
                    {{ activity.activity_number }}
                  </router-link>
                </td>

                <td>
                  <router-link :to="`/activity-details/${activity.slug}/${activity.id}`" class="fw-medium link-primary">
                    {{ activity.title }}
                  </router-link>
                </td>

                <td v-if="activity.description" v-html="activity.description" class="text-muted limit-20-chars text-truncate"></td>

                <td>{{ displayUserName(activity.createdBy || activity.created_by) }}</td>

                <td class="assignedto">
                  <div class="avatar-group">
                    <template v-if="Array.isArray(activity.users) && activity.users.length">
                      <b-link
                        v-for="(member, i) in activity.users"
                        :key="i"
                        href="javascript:void(0);"
                        class="avatar-group-item"
                        v-b-tooltip.hover
                        :title="displayUserName(member)"
                      >
                        <div class="avatar-xss">
                          <span :class="`avatar-title bg-soft-${color(member.id)} text-${color(member.id)} rounded-circle`">
                            {{ initials(displayUserName(member)) }}
                          </span>
                        </div>
                      </b-link>
                    </template>
                    <template v-else>
                      <span class="text-muted">—</span>
                    </template>
                  </div>
                </td>

                <td>{{ formatDate(activity.due_date) }}</td>

                <td>
                  <span class="badge" :class="statusBadgeClass(activity.status)">
                    {{ activity.status || '—' }}
                  </span>
                </td>

                <td>
                  <span class="badge text-uppercase" :class="priorityBadgeClass(activity.priority)">
                    {{ activity.priority || '—' }}
                  </span>
                </td>

                <td>
                  <ul class="list-inline mb-0">
                    <li class="list-inline-item">
                      <b-link :to="`/activity-details/${activity.slug}/${activity.id}`">
                        <i class="ri-eye-fill align-bottom me-2 text-muted"></i>
                      </b-link>
                    </li>
                    <!-- <li class="list-inline-item">
                      <b-link @click.prevent="openEditModal(activity)">
                        <i class="ri-pencil-fill align-bottom me-2 text-muted"></i>
                      </b-link>
                    </li> -->
                    <!-- <li class="list-inline-item">
                      <b-link
                        class="remove-item-btn"
                        @click.prevent="confirmDelete(activity)"
                        :aria-disabled="loading"
                        :class="{ disabled: loading }"
                      >
                        <i class="ri-delete-bin-fill align-bottom me-2 text-muted"></i>
                      </b-link>
                    </li> -->
                  </ul>
                </td>
              </tr>
            </tbody>
          </table>

          <div v-if="!filteredActivities.length" class="text-center py-4">
            <lottie :options="defaultOptions" :height="100" :width="100" />
            <h5 class="mt-2">No Activities found</h5>
          </div>
        </div>

        <div class="d-flex justify-content-between align-items-center">
          <b-pagination
            v-model="page"
            :per-page="perPage"
            :total-rows="totalRows"
            align="right"
            @input="fetchActivities"
          />
        </div>
      </div>
    </b-card>

    <!-- Add/Edit Modal -->
    <b-modal
      v-model="showModal"
      size="xl"
      class="v-modal-custom"
      modal-class="zoomIn"
      :title="isEditing ? 'Edit Activity' : 'Add Activity'"
      hide-footer
      header-class="p-3 bg-soft-info activityModal"
      centered
    >
      <form @submit.prevent="handleSubmit">
        <b-row>
          <b-col md="12">
            <b-form-group label="Title">
              <b-form-input v-model="activityForm.title" :state="!v$.activityForm.title.$error" />
              <b-form-invalid-feedback v-if="v$.activityForm.title.$error">
                <div v-for="(err, idx) in v$.activityForm.title.$errors" :key="idx">{{ err.$message }}</div>
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>

          <b-col md="12">
            <b-form-group label="Description">
              <ckeditor :editor="editor" v-model="activityForm.description" />
              <div class="text-danger" v-if="v$.activityForm.description.$error">
                <div v-for="(err, idx) in v$.activityForm.description.$errors" :key="idx">{{ err.$message }}</div>
              </div>
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group label="Priority">
              <b-form-select v-model="activityForm.priority" :options="priorityOptions" :state="!v$.activityForm.priority.$error" />
              <b-form-invalid-feedback v-for="(error, index) in v$.activityForm.priority.$errors" :key="index">
                {{ error.$message }}
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group label="Status">
              <b-form-select v-model="activityForm.status" :options="statusOptions" />
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group label="Start Date">
              <flat-pickr v-model="activityForm.started_at" class="form-control" :config="{ dateFormat: 'Y-m-d' }" />
              <div class="text-danger" v-if="v$.activityForm.started_at.$error">
                <div v-for="(err, idx) in v$.activityForm.started_at.$errors" :key="idx">{{ err.$message }}</div>
              </div>
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group label="Due Date">
              <flat-pickr v-model="activityForm.due_date" class="form-control" :config="{ dateFormat: 'Y-m-d' }" />
              <div class="text-danger" v-if="v$.activityForm.due_date.$error">
                <div v-for="(err, idx) in v$.activityForm.due_date.$errors" :key="idx">{{ err.$message }}</div>
              </div>
            </b-form-group>
          </b-col>
        </b-row>

        <div class="text-center" v-if="loading">
          <b-spinner variant="info" label="Loading..."></b-spinner>
        </div>
        <div class="text-end">
          <b-button type="submit" variant="success">{{ isEditing ? 'Update' : 'Create' }} Activity</b-button>
          <b-button type="button" variant="danger" class="ms-2" @click="closeModal">Cancel</b-button>
        </div>
      </form>
    </b-modal>
  </div>

</template>

<script>

import { CountTo } from "vue3-count-to";
import Multiselect from "@vueform/multiselect";
import "@vueform/multiselect/themes/default.css";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import CKEditor from "@ckeditor/ckeditor5-vue";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import Lottie from "@/components/widgets/lottie.vue";
import animationData from "@/components/widgets/msoeawqm.json";

import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";

import Swal from "sweetalert2";
import ActivityService from "@/services/activities.service.js";

export default {
  name: "ActivitiesList",
  components: {  CountTo, Multiselect, flatPickr, ckeditor: CKEditor.component, lottie: Lottie },
  props: {
    parentType: { type: String, default: "project" }, // 'project' | 'program' | 'department'
    parentId: { type: [Number, String], required: true },
    perPage: { type: Number, default: 8 },
  },
  emits: ["created", "updated", "deleted", "error"],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {

      title: "User Activities",
      items: [
        {
          text: "User Activities",
          active: true,
        },
      ],
      loading: false,
      allActivities: [],
      totalRows: 0,
      statsCards: [],
      page: 1,
      isFilterActive: false,
      searchTimeout: null,
      searchQuery: "",
      filterDateRange: null,
      filterStatus: null,

      // Keep a clean, DB-aligned status list
      statusSelectOptions: ["All", "unassigned", "todo", "in-progress", "in-reviews", "completed", "blocked"],

      showModal: false,
      isEditing: false,
      editor: ClassicEditor,
      activityForm: {
        id: null,
        title: "",
        activityNumber: "",
        description: "",
        priority: "",
        status: "",
        due_date: "",
        started_at: "",
        completed_at: "",
        assigned_to: "",
        department_id: "",
        project_id: null, // filled only if parentType === 'project'
      },
      defaultOptions: { animationData },
      rangeDateconfig: { mode: "range", altFormat: "M j, Y", altInput: true, dateFormat: "Y-m-d" },
      priorityOptions: ["low", "medium", "high", "urgent"],
      statusOptions: ["unassigned", "todo", "in-progress", "in-reviews", "completed", "blocked"],
    };
  },
  validations() {
    return {
      activityForm: {
        title: { required },
        description: { required },
        priority: { required },
        status: { required },
        due_date: { required },
        started_at: { required },
      },
    };
  },
  created() {
    this.fetchActivities(true);
  },
  watch: {
    searchQuery() { this.page = 1; },
    filterStatus() { this.page = 1; this.fetchActivities(); },
    filterDateRange() { this.page = 1; this.fetchActivities(); },
    parentId() { this.page = 1; this.fetchActivities(true); },
    parentType() { this.page = 1; this.fetchActivities(true); },
    page() { /* server-side pagination could be added here; for now we slice client-side */ },
  },
  computed: {
    filteredActivities() {
      let filtered = [...this.allActivities];

      if (this.searchQuery) {
        const s = this.searchQuery.toLowerCase();
        filtered = filtered.filter(activity =>
          Object.values(activity).some(val => String(val ?? "").toLowerCase().includes(s))
        );
      }

      if (this.filterStatus && this.filterStatus !== "All") {
        filtered = filtered.filter(activity => (activity.status || "").toLowerCase() === this.filterStatus.toLowerCase());
      }

      if (this.filterDateRange && this.filterDateRange.length === 2) {
        const [start, end] = this.filterDateRange.map(d => new Date(d));
        filtered = filtered.filter(activity => {
          const due = new Date(activity.due_date);
          return !isNaN(due) && due >= start && due <= end;
        });
      }

      const startIndex = (this.page - 1) * this.perPage;
      // this.totalRows = filtered.length;
      return filtered.slice(startIndex, startIndex + this.perPage);
    },
  },
  methods: {
    // ---------- UI helpers ----------
    formatDate(value) {
      if (!value) return "—";
      const d = new Date(value);
      if (isNaN(d)) return value;
      const opts = { year: "numeric", month: "short", day: "numeric" };
      return d.toLocaleDateString(undefined, opts);
    },
    statusBadgeClass(status) {
      if (!status) return "badge-soft-secondary";
      const s = status.toLowerCase();
      if (s === "in-progress") return "badge-soft-info";
      if (s === "in-reviews") return "badge-soft-primary";
      if (s === "completed") return "badge-soft-success";
      if (s === "blocked") return "badge-soft-danger";
      if (s === "unassigned") return "badge-soft-warning";
      if (s === "todo") return "badge-soft-secondary";
      return "badge-soft-secondary";
    },
    initials(name = "") {
      return name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map(s => s[0]?.toUpperCase() ?? "")
        .join("");
    },
    color(seed) {
      const arr = ["primary", "secondary", "success", "danger", "warning", "info", "dark"];
      const i = Math.abs(this.hash(String(seed))) % arr.length;
      return arr[i];
    },
    hash(s) {
      let h = 0;
      for (let i = 0; i < s.length; i++) { h = ((h << 5) - h) + s.charCodeAt(i); h |= 0; }
      return h;
    },
    priorityBadgeClass(priority) {
      if (!priority) return "bg-secondary";
      const p = priority.toLowerCase();
      if (p === "urgent" || p.includes("high")) return "bg-danger";
      if (p.includes("medium")) return "bg-warning";
      if (p.includes("low")) return "bg-success";
      return "bg-secondary";
    },
    displayUserName(user) {
      if (!user) return "—";
      const f = user.first_name ?? user.firstName ?? "";
      const l = user.last_name ?? user.lastName ?? "";
      const name = `${f} ${l}`.trim();
      return name || user.name || user.email || "—";
    },

    // ---------- Data I/O ----------
    async fetchActivities(initial = false) {
      this.loading = true;
      try {
        const params = {
          parent_type: this.parentType,
          parent_id: this.parentId,
          per_page: 1000, // grab enough to filter client-side; switch to server paging if needed
        };

        const resp = await ActivityService.getActivitiesByUser(this.$route.params.id,params);
        // console.log(resp.data);
        
        const payload = resp?.data ?? resp;

        // if (payload?.success !== true) {
        //   throw new Error(payload?.error || payload?.message || "Failed to fetch activities");
        // }

        const paged = payload.activities;
        const items = Array.isArray(paged) ? paged : (paged?.data ?? []);
        // console.log(items);
        
        this.allActivities = items;

        if (initial) {
          this.totalRows = Array.isArray(paged) ? items.length : (paged?.total ?? items.length);
        }

        const stats = payload.activityStatistics;
        if (stats) {
          this.statsCards = [
            { label: "Total Activities",    value: stats.total_activities ?? 0,    suffix: "", delta: "—", badgeClass: "text-success", icon: "ri-ticket-2-line",   avatarBg: "bg-soft-info text-info" },
            { label: "Pending Activities",  value: stats.pending_activities ?? 0,  suffix: "", delta: "—", badgeClass: "text-danger",  icon: "mdi mdi-timer-sand", avatarBg: "bg-soft-warning text-warning" },
            { label: "Completed Activities",value: stats.completed_activities ?? 0, suffix: "", delta: "—", badgeClass: "text-danger",  icon: "ri-checkbox-circle-line", avatarBg: "bg-soft-success text-success" },
            { label: "Over Due Activities", value: stats.overdue_activities ?? 0,  suffix: "", delta: "—", badgeClass: "text-danger",  icon: "mdi mdi-timer-sand", avatarBg: "bg-soft-danger text-danger" },
          ];
        }
      } catch (error) {
        console.error("Fetch error:", error);
        this.$emit("error", error?.message || "Error loading Activities");
        this.$toast?.error(error?.message || "Error loading Activities");
      } finally {
        this.loading = false;
      }
    },

    resetFilters() {
      this.searchQuery = "";
      this.filterStatus = null;
      this.filterDateRange = null;
      this.page = 1;
    },
    onSearchInput() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => { this.page = 1; }, 300);
    },

    openCreateModal() {
      this.resetForm();
      this.isEditing = false;
      this.showModal = true;
    },

    openEditModal(activity) {
      this.activityForm = {
        id: activity.id,
        title: activity.title || "",
        activityNumber: activity.activityNumber || activity.activity_number || "",
        description: activity.description || "",
        priority: activity.priority || "",
        status: activity.status || "",
        due_date: activity.dueDate || activity.due_date || "",
        started_at: activity.started_at || "",
        completed_at: activity.completed_at || "",
        assigned_to: activity.assigned_to || "",
        department_id: activity.department_id || "",
        project_id: this.parentType === "project" ? Number(this.parentId) : activity.project_id || null,
      };
      this.isEditing = true;
      this.showModal = true;
      this.v$.$reset();
    },

    closeModal() {
      this.showModal = false;
      this.resetForm();
    },

    resetForm() {
      this.activityForm = {
        id: null,
        title: "",
        activityNumber: "",
        description: "",
        priority: "",
        status: "",
        due_date: "",
        started_at: "",
        completed_at: "",
        assigned_to: "",
        department_id: "",
        project_id: this.parentType === "project" ? Number(this.parentId) : null,
      };
      this.v$?.$reset();
    },

    async handleSubmit() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        Swal.fire("Validation Error", "Please fill all required fields.", "error");
        return;
      }
      this.loading = true;
      try {
        // prefer generic parent on API
        const basePayload = {
          ...this.activityForm,
          parent_type: this.parentType,
          parent_id: Number(this.parentId),
        };
        // back-compat for backend expecting project_id when parent is project
        if (this.parentType === "project") basePayload.project_id = Number(this.parentId);

        if (this.isEditing && this.activityForm.id) {
          await ActivityService.updateActivty(this.activityForm.id, basePayload);
          Swal.fire("Success", "Activty updated successfully.", "success");
          this.$emit("updated", this.activityForm.id);
        } else {
          await ActivityService.createActivty(basePayload);
          Swal.fire("Success", "Activty created successfully.", "success");
          this.$emit("created");
        }

        this.showModal = false;
        this.resetForm();
        this.page = 1;
        await this.fetchActivities();
      } catch (err) {
        console.error("save error:", err);
        this.$emit("error", err?.message || "Failed to save activity.");
        Swal.fire("Error", "Failed to save activity.", "error");
      } finally {
        this.loading = false;
      }
    },

    async confirmDelete(activity) {
      try {
        const activityTitle = activity.activity || activity.title || "this activity";
        const result = await Swal.fire({
          title: "Are you sure?",
          text: `Do you want to delete the activity "${activityTitle}"?`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Yes, delete it!",
          cancelButtonText: "Cancel",
          reverseButtons: true,
          confirmButtonColor: "#e74c3c",
          cancelButtonColor: "#6c757d",
        });

        if (result.isConfirmed) {
          this.loading = true;
          await ActivityService.deleteActivity(activity.id);
          Swal.fire("Deleted!", "Activity has been deleted.", "success");
          this.$emit("deleted", activity.id);
          await this.fetchActivities();
        }
      } catch (error) {
        console.error("Delete failed:", error);
        this.$emit("error", error?.message || "Failed to delete activity.");
        Swal.fire("Error", "Failed to delete activity.", "error");
      } finally {
        this.loading = false;
      }
    },

  },
};
</script>

<style scoped>
.search-box { position: relative; }
.search-box .search-icon {
  position: absolute; right: 12px; top: 50%;
  transform: translateY(-50%); color: #6c757d; pointer-events: none; z-index: 1;
}
.search-box input { padding-right: 35px; width: 100%; border: 1px solid #dee2e6 !important; background-color: #f8f9fa !important; }
.search-box input.form-control { position: relative; z-index: 2; background-color: transparent !important; }

.table-card { min-height: 120px; }
.avatar-xxs { width: 26px; height: 26px; object-fit: cover; }
.disabled { pointer-events: none; opacity: 0.5; }

.limit-20-chars {
  max-width: 24ch; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
</style>
